import React, { ReactElement, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation, Trans } from "react-i18next";

import { logError } from "../../../helpers/logging";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import { ChallengeBadges_memberBadgesByCategory } from "../../../types/__generated__/ChallengeBadges";
import { ChildChallengeBadges } from "../../../types/__generated__/ChildChallengeBadges";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import {
  GET_CHILD_BADGES_FROM_CHALLENGE_BADGES,
  UPDATE_CHALLENGE_PROGRESS_STATUS,
} from "../../../graphql/resolvers/badge";
import { colors } from "../../../styles/variables";
import StyledSideTray from "../../atoms/SideTray/StyledSideTray";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import ChallengeProgress from "../../atoms/ChallengeProgress";
import ChallengeClaimedModal from "../ChallengeClaimedModal";
import Title from "../../atoms/Title";
import BadgeRelatedWines from "../BadgeRelatedWines";
import {
  I18N_NAMESPACES,
  DB_ID_CHALLENGE_STATUS_IN_PROGRESS,
  DB_ID_CHALLENGE_STATUS_ACHIEVED,
  DB_ID_CHALLENGE_STATUS_QUIT,
} from "../../../helpers/constants";
import badgeDefault from "../../../assets/images/icons/badge-default.svg";
import checkMark from "../../../assets/images/icons/check.svg";
import BadgeItem from "../BadgeItem";
import ChallengeDescriptionPoints from "../../atoms/ChallengeDescriptionPoints";
import { GET_MEMBER_POINTS_BALANCE } from "../../../graphql/resolvers/member";
import {
  UpdateChallengeProgressStatus,
  UpdateChallengeProgressStatusVariables,
} from "../../../types/__generated__/UpdateChallengeProgressStatus";
import { getChallengeBadgePoints } from "../../../helpers/tools";
import cross from "../../../assets/images/icons/cross.svg";
import backArrow from "../../../assets/images/icons/arrow-left-straight.svg";
import {
  Header,
  Cross,
  OptionsButton,
  OptionsButtonText,
  Cog,
  OuterContainer,
  ChildBadgeContainer,
  ChildBadgeWrapper,
  ChallengeBadgeImageContainer,
  ChallengeBadgeImageBackground,
  ChallengeBadgeImage,
  StatusTag,
  PointsText,
  StartButton,
  ClaimButton,
  StatusClaimedImage,
  AlertTitle,
  ChallengeDescription,
} from "./style";

interface ChallengeInfoTrayProps {
  memberId: number | null | undefined;
  challengeBadge: ChallengeBadges_memberBadgesByCategory | null | undefined;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  onClose: Function;
  onChildBadgeClick: Function;
  onWineDetailsClick: Function;
  onTopUpClick: Function;
  onUpdateChallengeStatus: Function;
  isChallengeBadgesQueryLoading: boolean;
  onOptionsClick: Function;
  isInBoxAndCartTray?: boolean;
  isBackArrow?: boolean;
}

const ChallengeInfoTray = ({
  memberId,
  challengeBadge,
  memberShoppingCartQuery,
  onClose,
  onChildBadgeClick,
  onWineDetailsClick,
  onTopUpClick,
  onUpdateChallengeStatus,
  isChallengeBadgesQueryLoading,
  onOptionsClick,
  isInBoxAndCartTray = false,
  isBackArrow = false,
}: ChallengeInfoTrayProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.MY_BADGES, I18N_NAMESPACES.MY_DASHBOARD]);

  const { data: childBadgesData, loading: isChildBadgesLoading } = useQuery<ChildChallengeBadges>(
    GET_CHILD_BADGES_FROM_CHALLENGE_BADGES,
    {
      skip: !challengeBadge?.id,
      variables: {
        memberId,
        challengeBadgeId: challengeBadge?.id,
      },
    }
  );

  const [updateChallengeProgressStatus, { loading: isUpdateChallengeStatusLoading }] = useMutation<
    UpdateChallengeProgressStatus,
    UpdateChallengeProgressStatusVariables
  >(UPDATE_CHALLENGE_PROGRESS_STATUS);

  const [shouldShowClaimedChallengeModal, setShouldShowClaimedChallengeModal] = useState(false);

  const isLoading = isChildBadgesLoading || isUpdateChallengeStatusLoading || isChallengeBadgesQueryLoading;

  const { memberChildBadgesFromChallengeBadge } = childBadgesData || {};

  // Badge challenge has manually been started.
  const hasStartedChallenge =
    !!challengeBadge?.challengeProgress?.challengeStatus &&
    challengeBadge?.challengeProgress?.challengeStatus?.id !== DB_ID_CHALLENGE_STATUS_QUIT;

  const totalBadgesFromChildBadges =
    (memberChildBadgesFromChallengeBadge && memberChildBadgesFromChallengeBadge.length) || 0;
  const numberOfTotalBadges =
    challengeBadge?.badgeRuleQuantity ||
    challengeBadge?.challengeProgress?.numberOfTotalBadges ||
    totalBadgesFromChildBadges;

  const numberOfEarnedBadges = challengeBadge?.challengeProgress?.numberOfEarnedBadges ?? 0;

  // Challenge badge has been earned.
  const isEarned =
    challengeBadge?.isEarned ||
    challengeBadge?.challengeProgress?.challengeStatus?.id === DB_ID_CHALLENGE_STATUS_ACHIEVED ||
    false;

  // Challenge badge can now be manually claimed.
  const hasCompletedChallenge =
    hasStartedChallenge && !isEarned && numberOfEarnedBadges >= numberOfTotalBadges;

  const shouldHideWineRecommendation = challengeBadge?.shouldHideWineRecommendation;

  const shouldShowBadgeRelatedWines =
    !!challengeBadge?.hasWineCharacteristicBadgeRule && hasStartedChallenge && !shouldHideWineRecommendation;

  const canClaimBadgeOnStart = numberOfEarnedBadges >= numberOfTotalBadges;

  /**
   * Renders Badge Status of the challenge.
   * */
  const renderBadgeStatus = (): ReactElement => {
    if (!hasStartedChallenge) {
      return <StatusTag color={colors.silver}>{t("myBadges:challengeStatus.notStarted")}</StatusTag>;
    }

    if (hasCompletedChallenge && !isEarned) {
      return <StatusTag color={colors.warning}>{t("myBadges:challengeStatus.completed")}</StatusTag>;
    }

    if (isEarned) {
      return (
        <>
          <StatusClaimedImage src={checkMark} alt="check icon" />
          <StatusTag color={colors.warningSoft}>{t("myBadges:challengeStatus.earned")}</StatusTag>
        </>
      );
    }

    return <div />;
  };

  const submitUpdateChallengeProgressStatus = async (desiredChallengeProgressStatusId: number) => {
    if (!challengeBadge?.id) {
      return;
    }

    try {
      const { data: challengeStatusData } = await updateChallengeProgressStatus({
        variables: {
          input: {
            badgeId: challengeBadge?.id,
            challengeProgressId: challengeBadge?.challengeProgress?.id,
            desiredChallengeStatusId: desiredChallengeProgressStatusId,
          },
        },
        refetchQueries: [{ query: GET_MEMBER_POINTS_BALANCE }],
      });

      const {
        errors,
        isSuccessful,
        challengeProgress: progress,
      } = challengeStatusData?.updateChallengeProgressStatus || {};

      if (errors || !progress || !isSuccessful) {
        const error = errors?.[0]?.messages?.[0] || "Sorry, something went wrong.";
        showErrorAlert(error, {});
        throw Error(error);
      }

      if (progress?.challengeStatus?.id === DB_ID_CHALLENGE_STATUS_ACHIEVED) {
        setShouldShowClaimedChallengeModal(true);
      }

      if (progress?.challengeStatus?.id === DB_ID_CHALLENGE_STATUS_IN_PROGRESS) {
        const i18nKey = canClaimBadgeOnStart
          ? "myBadges:completedChallengeOnStart"
          : "myBadges:startChallengeConfirmation";
        const alertText = (
          <Trans
            i18nKey={i18nKey}
            components={{ bold: <AlertTitle /> }}
            values={{
              challengeTitle: challengeBadge?.name,
            }}
          />
        );
        showSuccessAlert(alertText, {});
      }

      onUpdateChallengeStatus();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error, { location: "ChallengeInfoTray", action: "UPDATE_CHALLENGE_PROGRESS_STATUS" });
    }
  };

  /**
   * Renders the start challenge button.
   * */
  const renderStartChallengeButton = (): ReactElement => (
    <StartButton onClick={() => submitUpdateChallengeProgressStatus(DB_ID_CHALLENGE_STATUS_IN_PROGRESS)}>
      {t("myBadges:startChallenge")}
    </StartButton>
  );

  const renderClaimRewardButton = () => (
    <ClaimButton onClick={() => submitUpdateChallengeProgressStatus(DB_ID_CHALLENGE_STATUS_ACHIEVED)}>
      {t("myBadges:claimReward")}
    </ClaimButton>
  );

  const onCloseModal = () => {
    setShouldShowClaimedChallengeModal(false);
  };

  /**
   * Renders the challenge info: image of challenge badge, status, progress, name and points of badge.
   * */
  const renderChallengeInfo = (): ReactElement => (
    <>
      <ChallengeBadgeImageContainer>
        <ChallengeBadgeImageBackground isEarned={challengeBadge?.isEarned ?? false}>
          <ChallengeBadgeImage
            src={challengeBadge?.imageSmallUrl ?? badgeDefault}
            isEarned={challengeBadge?.isEarned ?? false}
          />
        </ChallengeBadgeImageBackground>
        {renderBadgeStatus()}
      </ChallengeBadgeImageContainer>
      <ChallengeProgress
        challengeProgress={challengeBadge?.challengeProgress}
        numberOfTotalBadges={numberOfTotalBadges}
        numberOfEarnedBadges={numberOfEarnedBadges}
      />
      <Title tag={3} mbMedium mbMd center uppercase>
        {challengeBadge?.name ?? ""}
      </Title>
      <PointsText>
        {t("myDashboard:pointsShort", {
          points: getChallengeBadgePoints(challengeBadge || {}),
        })}
      </PointsText>
    </>
  );

  /**
   * Renders challenge description and selling points.
   * */
  const renderChallengeDescriptionPoints = (shouldShowTitle: boolean = false): ReactElement => (
    <ChallengeDescriptionPoints shouldShowTitle={shouldShowTitle} challengeBadge={challengeBadge} />
  );

  /**
   * Renders child badges linked to the challenge.
   * */
  const renderChildBadges = (): ReactElement => (
    <ChildBadgeContainer>
      {memberChildBadgesFromChallengeBadge &&
        memberChildBadgesFromChallengeBadge.map((childBadgeItem) => {
          const isChildBadgeEarned = childBadgeItem?.isEarned ?? false;
          const isEarnedBadge = isChildBadgeEarned && hasStartedChallenge;
          return (
            <ChildBadgeWrapper key={childBadgeItem?.id}>
              <BadgeItem
                key={childBadgeItem?.id}
                onClick={() => onChildBadgeClick(childBadgeItem)}
                title={childBadgeItem?.name}
                point={childBadgeItem?.points || 0}
                imageUrl={childBadgeItem?.imageSmallUrl || badgeDefault}
                isEarned={isEarnedBadge}
                showPointsOrWineLevel={false}
              />
            </ChildBadgeWrapper>
          );
        })}
    </ChildBadgeContainer>
  );

  const shouldShowDescriptionTitle = true;

  /**
   * Renders the content for challenge details side tray.
   * */
  const renderContent = (): ReactElement => (
    <>
      <OuterContainer isBadgeRelatedWinesShown={shouldShowBadgeRelatedWines}>
        {!!challengeBadge && (
          <>
            {renderChallengeInfo()}
            {!hasStartedChallenge && renderStartChallengeButton()}
            <ChallengeDescription>{challengeBadge?.shortDescription ?? ""}</ChallengeDescription>
            {hasCompletedChallenge && renderClaimRewardButton()}
            {!hasStartedChallenge && renderChallengeDescriptionPoints()}
            {renderChildBadges()}
            {hasStartedChallenge
              ? renderChallengeDescriptionPoints(shouldShowDescriptionTitle)
              : renderStartChallengeButton()}
          </>
        )}
      </OuterContainer>
      {shouldShowBadgeRelatedWines && (
        <BadgeRelatedWines
          shouldConcealWines={false}
          memberChildBadgesFromChallengeBadge={memberChildBadgesFromChallengeBadge}
          badge={challengeBadge}
          onWineDetailsClick={onWineDetailsClick}
          onTopUpClick={onTopUpClick}
          memberShoppingCartQuery={memberShoppingCartQuery}
          isBadgeEarned={isEarned}
        />
      )}
      {shouldShowClaimedChallengeModal && (
        <ChallengeClaimedModal
          onCancel={onCloseModal}
          challengeRewardPoints={challengeBadge?.points || 0}
          challengeTitle={challengeBadge?.name || ""}
        />
      )}
    </>
  );

  return (
    <StyledSideTray
      isOpened={!!challengeBadge}
      color={colors.white}
      shouldAnimate
      onClickBackdrop={() => onClose()}
      isInBoxAndCartTray={isInBoxAndCartTray}
    >
      <Header>
        <Cross onClick={() => onClose()} src={isBackArrow ? backArrow : cross} />
        {hasStartedChallenge && (
          <OptionsButton onClick={() => onOptionsClick()}>
            <Cog />
            <OptionsButtonText>{t("myBadges:options")}</OptionsButtonText>
          </OptionsButton>
        )}
      </Header>
      {isLoading ? <StyledLoading /> : renderContent()}
    </StyledSideTray>
  );
};

export default ChallengeInfoTray;
